/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Provider as NiceModalProvider } from '@ebay/nice-modal-react';
import { ThemeProvider } from '@mui/material';
import { type ReactNode, useCallback, useRef } from 'react';
import { createCustomizedTheme } from '../../theme/createCustomizedTheme';
import { withErrorBoundary } from 'react-error-boundary';
import { ErrorFallback } from '../ErrorFallback/ErrorFallback';
import { triggerGTMEventApp } from '../../utils/triggerGTMEvent/triggerGTMEvent';
import { CKEditor5Builder } from '../CKEditor5Builder/CKEditor5Builder';
import type { CKEditor5BuilderProps } from '../CKEditor5Builder/types';
import { GTMContext } from '../../contexts/GTMContext';
import { CustomerPortalIntegration } from '../CustomerPortalIntegration/CustomerPortalIntegration';
import { ToastProvider } from '../ToastProvider/ToastProvider';
import { EvaluationLicense } from '../EvaluationLicense/EvaluationLicense';

export const CKEditor5BuilderWrapper = withErrorBoundary( _CKEditor5BuilderWrapper, { FallbackComponent: ErrorFallback } );

function _CKEditor5BuilderWrapper( CKEditor5BuilderProps: CKEditor5BuilderProps ): ReactNode {
	const themeCallback = useCallback( createCustomizedTheme, [] );
	const shouldEmitConversionEvent = useRef( true );
	const theme = themeCallback();

	return (
		<GTMContext.Provider value={ { triggerGTMEvent: triggerGTMEventApp, shouldEmitConversionEvent } }>
			<ThemeProvider theme={ theme }>
				<NiceModalProvider>
					<CustomerPortalIntegration>
						<ToastProvider>
							<EvaluationLicense>
								<CKEditor5Builder { ...CKEditor5BuilderProps }/>
							</EvaluationLicense>
						</ToastProvider>
					</CustomerPortalIntegration>
				</NiceModalProvider>
			</ThemeProvider>
		</GTMContext.Provider>
	);
}
